import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { generate } from 'shortid';

import Error from './Error';
import Label from './Label';

// Styles
import style from '../../../../scss/components/form.module.scss';

const Field = ({
    name,
    resource,
    children,
    errors,
    path = null,
    disabled = false,
    unlabeled = false,
    recursionLevel = null,
    changedFields,
    showDescription = false,
}) => {
    const [id] = useState(generate());
    const { t } = useTranslation();

    // Fully Qualified Path
    const FQP = `${path ? `${path}.${name}` : name}`;
    const LOCALE = `${FQP}${recursionLevel ? `-${recursionLevel}` : ''}`;

    const placeholder = t(
        `${resource}.${LOCALE}`.replace(new RegExp('[.][0-9]+', 'g'), ''),
    );

    const notIn = (errors) =>
        Object.entries(errors)
            .filter(([key]) => new RegExp(`${FQP}.[0-9]+.id`).test(key))
            .map(([, error]) => error);

    const error = errors
        ? errors[`${FQP}.id`] || errors[FQP] || notIn(errors)[0] || ''
        : '';

    const attributes = { id, name, disabled, placeholder };

    const hasError = !!error;
    const isChanged = changedFields.includes(name);

    return (
        <>
            {!unlabeled && (
                <Label
                    className={`${style.label} ${
                        hasError ? style.isInvalid : ''
                    }`}
                    to={id}
                    text={placeholder}
                />
            )}
            {children(hasError, attributes, isChanged)}
            {errors && <Error message={error} />}
            {showDescription && (
                <p className="text-lg">
                    {t(`${resource}.${name}_field_description`)}
                </p>
            )}
        </>
    );
};

export default Field;

Field.propTypes = {
    /**
     * Type of name
     */
    name: PropTypes.string.isRequired,
    /**
     * Type of resource
     */
    resource: PropTypes.string.isRequired,
    /**
     * Type of children
     */
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    /**
     * Type of errors
     */
    errors: PropTypes.object,
    /**
     * Type of path
     */
    path: PropTypes.object,
    /**
     * Type of disabled
     */
    disabled: PropTypes.bool,
    /**
     * Type of unlabeled
     */
    unlabeled: PropTypes.bool,
    /**
     * Type of recursionLevel
     */
    recursionLevel: PropTypes.array,
    /**
     * Type of changedFields
     */
    changedFields: PropTypes.array,
    /**
     * Type of showDescription
     */
    showDescription: PropTypes.bool,
};

Field.defaultProps = {
    errors: null,
    path: null,
    disabled: false,
    unlabeled: false,
    recursionLevel: null,
    changedFields: [],
    showDescription: false,
};
